/**
 * The robot's status, twice: as a ref for the sixty-times-a-second render loop in the headset,
 * and as React state for the pages, throttled so a 20 Hz stream does not rerender a form.
 */
import { json, useSubscription } from "adamo-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { statusTopic } from "./topics";
import type { Status } from "./types";

const UI_HZ = 8;

export type StatusFeed = {
  /** Always the newest, read from the animation loop without going through React. */
  current: () => Status | null;
  /** When the last sample arrived, on the browser's monotonic clock. */
  age: () => number;
};

export function useStatus(org: string | null, robot: string) {
  const latest = useRef<Status | null>(null);
  const at = useRef(0);
  const [shown, setShown] = useState<Status | null>(null);
  const nextRender = useRef(0);

  // Disabled rather than pointed at a placeholder key: everything outside `adamo/{org}/` is
  // refused by the relay ("authorization denied"), and a refusal here is silent.
  useSubscription(org ? statusTopic(org, robot) : "", (sample) => {
    const status = json.decode<Status>(sample.payload().toBytes());
    latest.current = status;
    at.current = performance.now();
    if (at.current >= nextRender.current) {
      nextRender.current = at.current + 1000 / UI_HZ;
      setShown(status);
    }
  }, { enabled: Boolean(org) });

  // The throttle above drops the final sample of a burst, which is the one that says the
  // arm stopped; a slow tick behind it makes sure the page settles on the truth.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (latest.current !== shown) setShown(latest.current);
    }, 400);
    return () => window.clearInterval(timer);
  }, [shown]);

  const feed: StatusFeed = useMemo(() => ({
    current: () => latest.current,
    age: () => (at.current === 0 ? Infinity : (performance.now() - at.current) / 1000),
  }), []);

  const staleFor = useCallback(() => feed.age(), [feed]);
  return { status: shown, feed, staleFor };
}
